import { ReactNode } from 'react';

import {
  BoxProps,
  Flex,
  FlexProps,
  SystemStyleObject,
  Tab,
  TabList,
  TabListProps,
  TabProps,
  Tabs,
  TabsProps,
} from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { SectionHeader } from '~/components/section-header';

export type TabbedSectionTab = {
  id: string;
  label: string;
  icon?: ReactNode;
  selectedColor?: string;
};

export type TabbedSectionProps = {
  bgColor?: string;
  sectionPadding?: FlexProps['padding'];
  sectionGap?: FlexProps['gap'];
  headerIcon?: ReactNode;
  headerSubtitle?: string;
  headerTitle: ReactNode;
  headerDescription?: ReactNode;
  headerColor?: string;
  tabs: TabbedSectionTab[];
  children: ReactNode;
  onTabChange?: (index: number) => void;
  tabsContentMaxWidth?: 'content-max-width' | 'content-max-width-lg' | 'content-max-width-md' | 'content-max-width-xl';
  tabsContainerProps?: BoxProps;
  tabsFooter?: ReactNode;
  tabsProps?: Partial<TabsProps>;
  tabListProps?: Partial<TabListProps>;
  tabProps?: Partial<TabProps>;
  /**
   * Merged into each tab's `_selected` so dark-themed sections can override the
   * default white pill. A tab's own `selectedColor` still wins for `color`.
   */
  tabSelectedProps?: SystemStyleObject;
};

export function TabbedSection({
  bgColor = 'grey.50',
  sectionPadding = { base: '56px 0px', md: '112px 0px' },
  sectionGap = { base: '40px', md: '72px' },
  headerIcon,
  headerSubtitle,
  headerTitle,
  headerDescription,
  headerColor = 'grey.500',
  tabs,
  children,
  onTabChange,
  tabsContentMaxWidth = 'content-max-width-md',
  tabsContainerProps,
  tabsFooter,
  tabsProps,
  tabListProps,
  tabProps,
  tabSelectedProps,
}: TabbedSectionProps) {
  return (
    <Flex as='section' bgColor={bgColor} flexDirection='column' gap={sectionGap} padding={sectionPadding}>
      <ContentContainer display='flex' flexDirection='column' gap='40px'>
        <SectionHeader
          color={headerColor}
          descriptionSlot={headerDescription}
          iconSlot={headerIcon}
          subtitle={headerSubtitle}
          titleSlot={headerTitle}
        />
      </ContentContainer>
      <ContentContainer contentMaxWidth={tabsContentMaxWidth} {...tabsContainerProps}>
        <Tabs colorScheme='blue' variant='soft-rounded' onChange={onTabChange} {...tabsProps}>
          <TabList
            bgColor='grey.100'
            borderRadius={{ md: '8px' }}
            justifyContent='space-between'
            mb={2}
            mx={{ base: '-16px', md: 'unset' }}
            overflow='auto'
            padding='6px'
            sx={{ scrollbarWidth: 'thin' }}
            {...tabListProps}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                alignItems='center'
                borderRadius='4px'
                color='grey.500'
                display='flex'
                flex={1}
                fontSize='0.8125rem'
                fontWeight='500'
                gap='8px'
                lineHeight='1.4'
                padding='14px 21px'
                whiteSpace='nowrap'
                {...tabProps}
                _selected={{
                  bgColor: 'white',
                  boxShadow: '0px 0px 12px 0px #00000014',
                  ...tabSelectedProps,
                  color: tab.selectedColor ?? tabSelectedProps?.color ?? 'blue.500',
                }}
              >
                {tab.icon}
                {tab.label}
              </Tab>
            ))}
          </TabList>
          {children}
        </Tabs>
        {tabsFooter}
      </ContentContainer>
    </Flex>
  );
}
